import { useRef, useState } from 'react'
import type React from 'react'
import type { AttachmentInfo } from '../../api/entries'
import { SchemePicker } from '../SchemePicker'
import type { ColorTheme } from './colorTheme'
import { useMediaUpload, markdownTag } from './useMediaUpload'
import { MediaPicker } from './MediaPicker'
import { TagInput } from './TagInput'
import { MarkdownRenderer } from './MarkdownRenderer'
import { TodoEntryDialog } from './TodoEntryDialog'

export type { ColorTheme }

const HATCH: React.CSSProperties = {
  border: '1px solid var(--color-fg)',
  padding: '1rem',
}

interface Props {
  name: string
  onNameChange: (v: string) => void
  namePlaceholder?: string
  content: string
  onContentChange: (v: string) => void
  showContent?: boolean
  contentRows?: number
  grow?: boolean
  privacy: number
  onPrivacyChange: (v: number) => void
  colorTheme: ColorTheme | null
  onColorThemeChange: (v: ColorTheme | null) => void
  username?: string | null
  allowReplies?: boolean
  onAllowRepliesChange?: (v: boolean) => void
  saving: boolean
  submitLabel?: string
  savingLabel?: string
  onSubmit: (e: React.FormEvent) => void
  onCancel: () => void
  style?: React.CSSProperties
  existingAttachments?: AttachmentInfo[]
  onDeleteExistingAttachment?: (id: string) => void
  showAttachButton?: boolean
  tags?: { name: string; tagger_username: string }[]
  onAddTag?: (tag: string) => void
  onRemoveTag?: (tag: string) => void
  onArchive?: () => void
}

export function EntryEditForm({
  name, onNameChange, namePlaceholder,
  content, onContentChange,
  showContent = true, contentRows = 8, grow = false,
  privacy, onPrivacyChange,
  colorTheme, onColorThemeChange,
  username = null,
  allowReplies, onAllowRepliesChange,
  saving, submitLabel = 'save', savingLabel = 'saving…',
  onSubmit, onCancel,
  style,
  existingAttachments,
  onDeleteExistingAttachment,
  showAttachButton = false,
  tags,
  onAddTag,
  onRemoveTag,
  onArchive,
}: Props) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { fileInputRef, handleFileChange, handlePaste, insertAtCursor, uploadingCount, uploadError } = useMediaUpload({
    username,
    textareaRef,
    content,
    setContent: onContentChange,
  })
  const [showPicker, setShowPicker] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [todoDialogOpen, setTodoDialogOpen] = useState(false)
  const [todoInsertPos, setTodoInsertPos] = useState(0)

  function insertTodo() {
    const el = textareaRef.current
    if (!el) return
    if (username) {
      setTodoInsertPos(el.selectionStart)
      setTodoDialogOpen(true)
    } else {
      const start = el.selectionStart
      const before = content.slice(0, start)
      const after = content.slice(el.selectionEnd)
      const needsNewline = before.length > 0 && !before.endsWith('\n')
      const insertion = (needsNewline ? '\n' : '') + '- [ ] '
      onContentChange(before + insertion + after)
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = start + insertion.length
        el.focus()
      })
    }
  }

  function handleTodoInserted(markdown: string) {
    const el = textareaRef.current
    const pos = todoInsertPos
    const before = content.slice(0, pos)
    const after = content.slice(pos)
    const needsNewline = before.length > 0 && !before.endsWith('\n')
    const insertion = (needsNewline ? '\n' : '') + markdown
    onContentChange(before + insertion + after)
    setTodoDialogOpen(false)
    requestAnimationFrame(() => {
      if (el) {
        el.selectionStart = el.selectionEnd = pos + insertion.length
        el.focus()
      }
    })
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Enter') return
    const el = textareaRef.current
    if (!el) return
    const pos = el.selectionStart
    if (pos !== el.selectionEnd) return

    const lineStart = content.lastIndexOf('\n', pos - 1) + 1
    const currentLine = content.slice(lineStart, pos)
    const match = currentLine.match(/^- \[[ xX]\] (.*)$/)
    if (!match) return

    e.preventDefault()
    const afterPrefix = match[1]

    if (afterPrefix.trim() === '') {
      const newContent = content.slice(0, lineStart) + content.slice(pos)
      onContentChange(newContent)
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = lineStart
      })
    } else {
      const insertion = '\n- [ ] '
      const newContent = content.slice(0, pos) + insertion + content.slice(el.selectionEnd)
      onContentChange(newContent)
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = pos + insertion.length
      })
    }
  }

  const imageAttachments = existingAttachments?.filter((a) => a.mimetype.startsWith('image/')) ?? []
  const fileAttachments = existingAttachments?.filter((a) => !a.mimetype.startsWith('image/')) ?? []

  return (
  <>
    <form
      onSubmit={onSubmit}
      style={{
        ...HATCH,
        ...style,
        ...(grow ? { display: 'flex', flexDirection: 'column', minHeight: 'calc(100svh - 180px)' } : {}),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', margin: '0.5rem 0' }}>
        <input
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder={namePlaceholder}
          style={{ flex: 1 }}
        />
        <select
          value={privacy}
          onChange={(e) => onPrivacyChange(Number(e.target.value))}
          style={{ width: 'auto' }}
        >
          <option value={0}>private</option>
          <option value={1}>friends</option>
          <option value={2}>friends of friends</option>
          <option value={3}>public</option>
        </select>
      </div>
      {showContent && (
        <div style={{ ...(grow ? { flex: 1, display: 'flex', flexDirection: 'column' } : {}) }}>
          <div style={{ position: 'relative', marginTop: '0.5rem', ...(grow ? { flex: 1, display: 'flex', flexDirection: 'column' } : {}) }}>
            <textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => onContentChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={showAttachButton ? handlePaste : undefined}
              rows={contentRows}
              style={{
                width: '100%',
                display: 'block',
                visibility: showPreview ? 'hidden' : 'visible',
                ...(grow ? { flex: 1, minHeight: '200px', resize: 'vertical' } : {}),
              }}
            />
            {showPreview && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  border: '1px solid var(--color-fg)',
                  padding: '0.5rem 1rem',
                  overflowY: 'auto',
                  background: 'var(--color-bg)',
                }}
              >
                {content
                  ? <MarkdownRenderer entryId="" authorUsername="" content={content} colorTheme={colorTheme} />
                  : <span style={{ opacity: 0.5 }}>nothing to preview</span>
                }
              </div>
            )}
          </div>
          <div style={{ border: '1px solid var(--color-fg)', borderTop: 'none', display: 'flex', alignItems: 'center', padding: '0.3rem 0.5rem', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <SchemePicker
              colorTheme={colorTheme}
              onColorThemeChange={onColorThemeChange}
              username={username}
            />
            <button
              type="button"
              onClick={() => setShowPreview((p) => {
                if (p) requestAnimationFrame(() => textareaRef.current?.focus())
                return !p
              })}
              style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.8rem' }}
            >
              {showPreview ? 'edit' : 'preview'}
            </button>
            {!showPreview && (
              <button
                type="button"
                onClick={insertTodo}
                style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.8rem', marginLeft: 'auto' }}
              >
                + todo
              </button>
            )}
            {!showPreview && showAttachButton && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  aria-label="attach files"
                />
                <button
                  type="button"
                  onClick={() => setShowPicker(true)}
                  style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.8rem' }}
                >
                  + media
                </button>
                {uploadingCount > 0 && (
                  <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>uploading…</span>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showAttachButton && (imageAttachments.length > 0 || fileAttachments.length > 0) && (
        <div style={{ marginBottom: '0.5rem', fontSize: '0.85rem', opacity: 0.8 }}>
          {imageAttachments.map((att) => (
            <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
              <span>{att.filename}</span>
              {onDeleteExistingAttachment && (
                <button
                  type="button"
                  onClick={() => onDeleteExistingAttachment(att.id)}
                  style={{ background: 'none', border: 'none', color: 'var(--color-fg)', cursor: 'pointer', padding: '0 0.2rem', lineHeight: 1, fontSize: '1rem' }}
                  aria-label={`remove attachment ${att.filename}`}
                >×</button>
              )}
            </div>
          ))}
          {fileAttachments.map((att) => (
            <div key={att.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
              <span>{att.filename}</span>
              {onDeleteExistingAttachment && (
                <button
                  type="button"
                  onClick={() => onDeleteExistingAttachment(att.id)}
                  style={{ background: 'none', border: 'none', color: 'var(--color-fg)', cursor: 'pointer', padding: '0 0.2rem', lineHeight: 1, fontSize: '1rem' }}
                  aria-label={`remove attachment ${att.filename}`}
                >×</button>
              )}
            </div>
          ))}
        </div>
      )}

      {onAddTag && (
        <div style={{ marginBottom: '0.5rem', marginTop: '1rem' }}>
          <TagInput
            username={username}
            onSubmit={onAddTag}
            excludeTags={tags?.map((t) => t.name)}
            disabled={saving}
            inputStyle={{ fontSize: '0.85rem' }}
            buttonStyle={{ width: 'auto', fontSize: '0.85rem' }}
          />
        </div>
      )}

      {tags && tags.length > 0 && (
        <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          {tags.map((tag) => (
            <span key={tag.name} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.1rem', background: 'var(--color-fg)', color: 'var(--color-bg)', padding: '0.1rem 0.4rem', borderRadius: 2, fontSize: '0.85rem' }}>
              #{tag.name}
              {onRemoveTag && (
                <button
                  type="button"
                  onClick={() => onRemoveTag(tag.name)}
                  style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 0.1rem', lineHeight: 1, fontSize: '1rem' }}
                  aria-label={`remove tag ${tag.name}`}
                >×</button>
              )}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="submit" disabled={saving}>{saving ? savingLabel : submitLabel}</button>
        <button type="button" onClick={onCancel} style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto' }}>cancel</button>
        {onAllowRepliesChange !== undefined && privacy !== 0 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={allowReplies ?? false}
              onChange={(e) => onAllowRepliesChange(e.target.checked)}
            />
            allow replies
          </label>
        )}
        {onArchive && (
          <button
            type="button"
            onClick={onArchive}
            className="btn-inverse"
            style={{ width: 'auto', marginLeft: 'auto' }}
          >
            archive
          </button>
        )}
      </div>
      {uploadError && (
        <p style={{ color: 'var(--color-1)', marginTop: '0.5rem' }}>{uploadError}</p>
      )}

      {showPicker && username && (
        <MediaPicker
          username={username}
          onSelect={(item) => {
            insertAtCursor(markdownTag(item.filename, item.url, item.mimetype))
            setShowPicker(false)
          }}
          onUpload={() => {
            setShowPicker(false)
            fileInputRef.current?.click()
          }}
          onClose={() => setShowPicker(false)}
        />
      )}
    </form>
    {todoDialogOpen && username && (
      <TodoEntryDialog
        defaultPrivacy={privacy}
        defaultColorTheme={colorTheme}
        username={username}
        onInserted={handleTodoInserted}
        onClose={() => setTodoDialogOpen(false)}
      />
    )}
  </>
  )
}
